// Per-order view (SM-755): which provider requests arrived for a Smart
// Market order. The contract carries no order id, so the mock derives
// the order's transaction ids (funding Deduct, compensation Rollback,
// unused-funds Credit) from (broker_account_id, order_id), tracks the
// order and STORES every answer as an observation — so "no provider
// requests received for this order" is recorded, exportable evidence,
// never a blank table.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { AttemptsTable } from "@/components/domain/AttemptsTable";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Check, Field, TextInput } from "@/components/ui/Field";
import { Empty, KV, Mono, PageHeader, TableWrap } from "@/components/ui/Misc";
import { api } from "@/lib/api";
import { ENDPOINTS } from "@/lib/constants";
import { endpointName, fmtTime, localToRfc3339 } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";
import type { OrderEvidence, OrderQuery } from "@/lib/types";

export const Route = createFileRoute("/_app/orders")({
  component: OrdersPage,
});

function OrdersPage() {
  const qc = useQueryClient();
  const [acct, setAcct] = useState("");
  const [order, setOrder] = useState("");
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [eps, setEps] = useState<string[]>([...ENDPOINTS]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<{ query: OrderQuery; evidence: OrderEvidence } | null>(null);

  const ok = acct.trim() !== "" && order.trim() !== "" && eps.length > 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Orders"
        subtitle="Prove which provider requests a Smart Market order produced — or that none arrived. Every lookup is stored as an observation (observations.csv)."
      />
      <SectionCard title="Look up an order">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!ok) return;
            const query: OrderQuery = {
              broker_account_id: acct.trim(),
              order_id: order.trim(),
              user_id: userId.trim() || undefined,
              endpoints: eps.length === ENDPOINTS.length ? [] : eps,
              window_start: localToRfc3339(from),
              window_end: localToRfc3339(to),
            };
            setBusy(true);
            setError(null);
            try {
              const evidence = await api<OrderEvidence>("/orders/lookup", { method: "POST", body: query });
              setResult({ query, evidence });
              await qc.invalidateQueries({ queryKey: ["mock", "observations"] });
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <fieldset disabled={busy}>
            <div className="form-grid">
              <Field label="Broker account id">
                <TextInput value={acct} onChange={(e) => setAcct(e.target.value)} className="mono" placeholder="bac_…" required />
              </Field>
              <Field label="Order id">
                <TextInput value={order} onChange={(e) => setOrder(e.target.value)} className="mono" placeholder="ord_…" required />
              </Field>
              <Field label="User id (optional)" hint="With a window, also counts every request for this user.">
                <TextInput value={userId} onChange={(e) => setUserId(e.target.value)} className="mono" />
              </Field>
              <Field label="Window start (optional)">
                <TextInput type="datetime-local" step="1" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field label="Window end (optional)">
                <TextInput type="datetime-local" step="1" value={to} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </div>
            <div className="mt-4">
              <div className="mb-2 text-2xs font-semibold uppercase tracking-wide text-t-3">Endpoints</div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {ENDPOINTS.map((ep) => (
                  <Check
                    key={ep}
                    label={<span className="mono text-xs">{endpointName(ep)}</span>}
                    checked={eps.includes(ep)}
                    onChange={(e) => setEps((cur) => (e.target.checked ? [...cur, ep] : cur.filter((x) => x !== ep)))}
                  />
                ))}
              </div>
              {eps.length === 0 && <p className="mt-1 text-2xs text-no">Pick at least one endpoint.</p>}
            </div>
            <ErrorBanner error={error} className="mt-3" />
            <div className="mt-4 flex justify-end">
              <Button type="submit" loading={busy} disabled={!ok}>
                Look up requests
              </Button>
            </div>
          </fieldset>
        </form>
      </SectionCard>

      {result && <Evidence query={result.query} ev={result.evidence} />}
      {result && <Observations orderKey={result.evidence.order_key} />}
    </div>
  );
}

function Evidence({ query, ev }: { query: OrderQuery; ev: OrderEvidence }) {
  const ids = ev.transaction_ids ?? [];
  const endpoints = ev.endpoints ?? [];
  const windowText =
    query.window_start || query.window_end
      ? `${fmtTime(query.window_start)} → ${fmtTime(query.window_end)}`
      : "No window — every request ever received";
  const facts = (
    <KV
      items={[
        ["Observation id", <Mono>#{ev.observation_id}</Mono>],
        ["Order key", <Mono>{ev.order_key}</Mono>],
        ["Window", windowText],
        ["Asked at", fmtTime(ev.asked_at)],
        ["Endpoints", <span className="mono text-xs">{endpoints.map(endpointName).join(", ")}</span>],
        ...(ev.user_window
          ? ([
              [
                `Requests for ${ev.user_window.user_id} in window`,
                <span className="mono font-semibold">{ev.user_window.requests_received}</span>,
              ],
            ] as Array<[string, ReactNode]>)
          : []),
      ]}
    />
  );
  const idList = (
    <div className="mt-4">
      <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-t-3">Derived transaction ids</div>
      <ul className="space-y-1">
        {ids.map((id, i) => (
          <li key={id} className="text-xs">
            <span className="t-mute">{["funding Deduct", "compensation Rollback", "unused-funds Credit"][i] ?? "id"}: </span>
            <Mono className="text-t-1">{id}</Mono>
          </li>
        ))}
      </ul>
    </div>
  );

  if (ev.no_requests_received) {
    return (
      <div className="no-requests" role="status" data-testid="no-requests">
        <div className="mb-1 text-2xs font-bold uppercase tracking-[0.14em]" style={{ color: "#116b3e" }}>
          Recorded evidence · observation #{ev.observation_id}
        </div>
        <h2>No provider requests received for this order</h2>
        <p className="mb-4 mt-1 text-sm text-t-2">
          Zero requests carrying any of this order's transaction ids reached the selected endpoints in the window. This
          answer is stored and exported in <span className="mono">observations.csv</span>.
        </p>
        {facts}
        {idList}
      </div>
    );
  }
  return (
    <SectionCard title={`${ev.requests_received} provider request${ev.requests_received === 1 ? "" : "s"} received for this order`}>
      {facts}
      {idList}
      <div className="mt-4">
        <AttemptsTable rows={ev.requests ?? []} />
      </div>
    </SectionCard>
  );
}

function Observations({ orderKey }: { orderKey: string }) {
  const obs = useQuery({ queryKey: keys.observations(orderKey), queryFn: () => fetchers.observations(orderKey) });
  const rows = obs.data ?? [];
  return (
    <SectionCard title="Stored observations for this order">
      <ErrorBanner error={obs.error} />
      {rows.length === 0 ? (
        <Empty>No observations stored.</Empty>
      ) : (
        <TableWrap>
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Asked</th>
                <th>By</th>
                <th>Kind</th>
                <th>Window</th>
                <th>Requests</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.observation_id}>
                  <td>{o.observation_id}</td>
                  <td>{fmtTime(o.asked_at)}</td>
                  <td>{o.asked_by}</td>
                  <td>{o.subject_kind}</td>
                  <td>{o.window_start || o.window_end ? `${fmtTime(o.window_start)} → ${fmtTime(o.window_end)}` : "all time"}</td>
                  <td>{o.requests_received}</td>
                  <td>{o.no_requests_received ? <span className="pill yes">none received</span> : <span className="pill warn">received</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </SectionCard>
  );
}
